import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, AssetCurfewParameter } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, TimeField, ComboField, CheckListField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';

interface EditorValues {
    curfews: ListFieldValue<AssetCurfewParameter>[];
}

const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export class AssetCurfewEventsSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = 'clock';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    properties = ['curfews'];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.CURFEW_EVENTS.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    async mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        const result: EditorValues = {
            curfews: (entity.parameters && entity.parameters.curfews && entity.parameters.curfews.map(curfew => this.curfewToListItem(curfew))) || [],
        };
        return result;
    }

    async mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {
        entity.parameters = entity.parameters || {};
        if (values.curfews) {
            entity.parameters.curfews = values.curfews.map(x => x.data);
        }
        return entity;
    }

    async getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        const fields: FormBuilderField[] = [
            {
                id: 'curfews', title: this.translate('FIELDS.CURFEWS'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddCurfewModal.bind(this), },
                    { id: 'edit', click: this.showAddCurfewModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ];
        return EntityUtils.prepareBulk(!entity, ['curfews'], fields);
    }

    mapValidationErrors(errors: { [field: string]: string }): { [field: string]: string } {

        const result = {};
        Object.keys(errors).forEach(field => {
            this.properties.forEach(prop => {
                if (field.startsWith(prop)) {
                    result[prop] = result[prop] || [];
                    result[prop].push(errors[field]);
                }
            });
        });
        return Object.keys(result).reduce((obj, key) => { obj[key] = result[key].join(', '); return obj; }, {});
    }

    curfewToListItem(curfew: AssetCurfewParameter): ListFieldValue<AssetCurfewParameter> {

        const name = `${curfew.from} - ${curfew.to}`;
        const desc = {
            from: curfew.from,
            to: curfew.to,
            days: curfew.days.map(index => this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${days[parseFloat(index)]}`)).join(', '),
        };
        return {
            id: curfew.id,
            name: name,
            icon: 'clock',
            description: this.translate('FIELDS.CURFEW_DESC', desc),
            data: curfew,
        };
    }

    updateOrAddItem<T extends { id: string }>(items: ListFieldValue<any>[], existingItem: ListFieldValue<any>, curfew: T, itemToListItem: (curfew: T) => ListFieldValue<any>) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(curfew);
        } else {
            items.push(itemToListItem(curfew));
        }
    }

    async showAddCurfewModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<AssetCurfewParameter> {

        const timeZones = await this.admin.getTimeZones();
        return this.admin.showFormModal<AssetCurfewParameter>({
            groups: [{
                name: this.translate('MODALS.CURFEW.TITLE'),
                description: this.translate('MODALS.CURFEW.DESC'),
                fields: [
                    TimeField('from', this.translate('FIELDS.FROM'), true),
                    TimeField('to', this.translate('FIELDS.TO'), true),
                    ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), true, timeZones.map(x => ({ key: x.id, value: x.name }))),
                    CheckListField('days', this.translate('FIELDS.DAYS'), true, days.map((key, index) => ({ key: index.toString(), value: this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${key}`) }))),
                ],
            }],
        }, (item && item.data) ||
            { // pass some defaults for new items
                id: uuid.v4(),
                from: '00:00:00',
                to: '23:59:00',
                timeZoneId: this.admin.user.timeZoneId,
                days: ['0', '1', '2', '3', '4', '5', '6'],
            }
        ).then(curfew => {
            if (curfew) {
                this.updateOrAddItem(values.curfews, item, curfew, this.curfewToListItem.bind(this));
            }
            return curfew;
        });

    }

}

